import React from "react";
import {
  ArrowLeft,
  X,
  Plus,
  FileText,
  Info,
  AlertTriangle,
  Check,
  Rocket,
  Eye,
  Heart,
  MessageCircle,
  Send,
  Bookmark,
  ThumbsUp,
  Share2,
  Globe,
  Lightbulb,
  Repeat2,
  MoreHorizontal,
  Loader2,
  FileEdit,
} from "lucide-react";
import { useStep3 } from "../../../../hooks/useStep3";
import { usePfmStep3 } from "../../../../hooks/usePfmStep3";
import { DraftVariations } from "../../../../types/creator-studio";
import Toast from "../../../uiComponents/Toast";
import "./step3.css";

import xIcon from "../../../../assets/images/social/x-twitter.svg";
import threadsIcon from "../../../../assets/images/social/threads.svg";
import redditIcon from "../../../../assets/images/social/reddit.svg";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
  TwitterForm,
  InstagramForm,
  FacebookForm,
  LinkedInForm,
  RedditForm,
} from "./platforms";
import { MediaCarousel } from "./media-carousel";
import { BrandDetails } from "../../../../types/add-brand-types";
import { SOCIAL_PLATFORMS } from "../../../../utils/platform-post-util";
import { ContentLog } from "../../../../types/dashboard";
import useCurrentPlan from "../../../../hooks/use-current-plan";
import usePaddleCheckout from "../../../../hooks/use-paddle-checkout";
import useProUpgradeModal from "../../../../hooks/use-pro-upgrade-modal";

export type SocialProvider = "getlate" | "postforme";

export interface SocialPostScreenProps {
  onBack: () => void;
  selectedVariation?: DraftVariations;
  brandId: string;
  brandDetails: BrandDetails;
  historyItem?: ContentLog;
  onPostSuccess?: () => void;
  prefilledScheduleDate?: Date;
  prefilledPlatforms?: string[];
  weeklyPlanItemId?: string;
  provider?: SocialProvider;
}

const SocialPostScreen: React.FC<SocialPostScreenProps> = ({
  onBack,
  selectedVariation,
  brandId,
  brandDetails,
  historyItem,
  onPostSuccess,
  prefilledScheduleDate,
  prefilledPlatforms,
  weeklyPlanItemId,
  provider = "getlate",
}) => {
  const lateStep3 = useStep3({
    selectedVariation,
    socialPlatforms: SOCIAL_PLATFORMS,
    brandId,
    brandDetails,
    historyItem,
    onPostSuccess,
    prefilledScheduleDate,
    prefilledPlatforms,
    weeklyPlanItemId,
  });

  const pfmStep3 = usePfmStep3({
    selectedVariation,
    socialPlatforms: SOCIAL_PLATFORMS,
    brandId,
    brandDetails,
    historyItem,
    onPostSuccess,
    prefilledScheduleDate,
    prefilledPlatforms,
    weeklyPlanItemId,
  });

  const step3 = provider === "postforme" ? pfmStep3 : lateStep3;
  const { plan, loading: loadingPlan } = useCurrentPlan(brandDetails.id);
  const { openCheckout, isOpeningCheckout, isConfigured, pricesConfigured } =
    usePaddleCheckout();
  const { openUpgradeModal, upgradeModal } = useProUpgradeModal({
    onUpgradeCheckout: openCheckout,
    isUpgradeDisabled: !isConfigured,
    isUpgradeLoading: isOpeningCheckout,
    currentPlanTier: plan?.planTier,
    pricesConfigured,
  });

  const {
    postTitle,
    setPostTitle,
    postBody,
    setPostBody,
    tweetBody,
    setTweetBody,
    scheduleOption,
    setScheduleOption,
    togglePlatform,
    selectedPlatforms,
    mediaPreviewUrls,
    mediaMetadata,
    useCommonMediaForAllPlatforms,
    setUseCommonMediaForAllPlatforms,
    handleMediaUpload,
    removeMedia,
    scheduledDate,
    setScheduledDate,
    activeFormTab,
    setActiveFormTab,
    showPlatformSelector,
    setShowPlatformSelector,
    fileInputRef,
    draggedIndex,
    toast,
    setToast,
    validationIssues,
    isLaunchDisabled,
    isLaunching,
    xLongFormEnabled,
    setXLongFormEnabled,
    xMaxCharacters,
    handleLaunch,
    handleSaveDraft,
    handleFileChange,
    handleDragOver,
    handleDrop,
    handleImageDragStart,
    handleImageDragOver,
    handleImageDrop,
    handleImageDragEnd,
    renderTextWithHashtags,
    viewMode,
    threadEnabled,
    setThreadEnabled,
    threadTweets,
    setThreadTweets,
    isStory,
    setIsStory,
    subreddits,
    setSubreddits,
    searchSubreddit,
  } = step3;

  const isPublished = viewMode === "published";
  const isScheduledView = viewMode === "scheduled";
  const isDraftView = viewMode === "draft";
  const isHistoryView = viewMode !== null;
  const isSchedulingLocked =
    !loadingPlan && !!plan && !plan.features.scheduling;
  const isScheduleLaterDisabled = loadingPlan || isSchedulingLocked;
  const accountsSource =
    provider === "postforme"
      ? brandDetails.pfm_social_profile_ids
      : brandDetails.social_profile_ids;
  const availablePlatforms = accountsSource?.accounts ?? [];

  React.useEffect(() => {
    if (isSchedulingLocked && !isScheduledView && scheduleOption === "later") {
      setScheduleOption("now");
    }
  }, [isScheduledView, isSchedulingLocked, scheduleOption, setScheduleOption]);

  const getPreviewAccount = (platformId: string) =>
    accountsSource?.accounts?.find(
      (a: {
        platform: string;
        username?: string;
        displayName?: string;
        profilePicture?: string;
      }) => a.platform?.toLowerCase() === platformId.toLowerCase()
    );

  const renderMediaGrid = (
    maxDisplay: number = 4,
    borderRadius: string = "0px"
  ) => {
    return (
      <MediaCarousel
        mediaPreviewUrls={mediaPreviewUrls}
        mediaMetadata={mediaMetadata}
        borderRadius={borderRadius}
      />
    );
  };

  const renderTwitterPreview = () => {
    const visibleThreadTweets =
      threadEnabled && threadTweets ? threadTweets.filter((t) => t.trim()) : [];
    const hasThread = visibleThreadTweets.length > 0;
    const account = getPreviewAccount("twitter") ?? getPreviewAccount("x");
    const displayName = account?.displayName ?? "Nexus AI";
    const username = account?.username ?? "nexus_ai";

    return (
      <div className="twitter-preview-container">
        {/* Twitter Header */}
        <div className="twitter-header">
          <div className="preview-avatar">
            {account?.profilePicture ? (
              <img
                src={account.profilePicture}
                alt={displayName}
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  background: "#1f2937",
                  borderRadius: "50%",
                }}
              />
            )}
          </div>
          <div className="twitter-user-info">
            <div className="twitter-name-row">
              <span className="twitter-name">{displayName}</span>
              <img
                src={xIcon}
                alt="X"
                style={{ width: 16, height: 16, opacity: 0.8 }}
                title="X (Twitter)"
              />
            </div>
            <span className="twitter-handle">@{username}</span>
          </div>
        </div>

        {/* Twitter Body */}
        <div className="twitter-body">
          <p>
            {renderTextWithHashtags(
              tweetBody ||
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. #innovation #ai"
            )}
          </p>
        </div>

        {/* Twitter Media - only show when there is media */}
        {mediaPreviewUrls.length > 0 && (
          <div className="twitter-media" style={{ overflow: "hidden" }}>
            {renderMediaGrid(4, "16px")}
          </div>
        )}

        {/* Twitter Meta */}
        <div className="twitter-meta">
          <span>11:30 PM · 21/03/2030 · </span>
          <span className="twitter-views">987K Views</span>
        </div>

        <div className="twitter-divider"></div>

        {/* Twitter Stats */}
        <div className="twitter-stats">
          <div className="stat-item">
            <Repeat2 size={14} style={{ opacity: 0.7 }} />
            <strong>82k</strong>
          </div>
          <div className="stat-item">
            <MessageCircle size={14} style={{ opacity: 0.7 }} />
            <strong>45k</strong>
          </div>
          <div className="stat-item">
            <Heart size={14} style={{ opacity: 0.7 }} />
            <strong>91k</strong>
          </div>
          <div className="stat-item">
            <Bookmark size={14} style={{ opacity: 0.7 }} />
            <strong>78k</strong>
          </div>
        </div>

        {/* Thread Replies Preview */}
        {hasThread &&
          visibleThreadTweets.map((tweet, idx) => {
            const isLast = idx === visibleThreadTweets.length - 1;
            return (
              <div key={idx}>
                <div className="twitter-divider" />
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    paddingTop: "12px",
                  }}
                >
                  {/* Avatar column — stretches full height */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      flexShrink: 0,
                      width: "32px",
                    }}
                  >
                    {account?.profilePicture ? (
                      <img
                        src={account.profilePicture}
                        alt={displayName}
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          background: "#1f2937",
                          borderRadius: "50%",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {/* Thread line — grows to fill remaining height */}
                    {!isLast && (
                      <div
                        style={{
                          width: "2px",
                          flex: 1,
                          background: "#cfd9de",
                          marginTop: "4px",
                          marginBottom: "-12px",
                          minHeight: "16px",
                        }}
                      />
                    )}
                  </div>
                  {/* Tweet content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        marginBottom: "2px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: "13px",
                          color: "#0f1419",
                        }}
                      >
                        {displayName}
                      </span>
                      <span
                        style={{
                          fontSize: "13px",
                          color: "#536471",
                        }}
                      >
                        @{username}
                      </span>
                    </div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "#0f1419",
                        lineHeight: "1.4",
                        wordBreak: "break-word",
                      }}
                    >
                      {renderTextWithHashtags(tweet)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    );
  };

  const renderInstagramPreview = () => {
    const igAccount = getPreviewAccount("instagram");
    const igUsername = igAccount?.username ?? "nexus_ai";

    if (isStory) {
      return (
        <div
          style={{
            width: "100%",
            maxWidth: "280px",
            margin: "0 auto",
            aspectRatio: "9/16",
            borderRadius: "12px",
            overflow: "hidden",
            background:
              "linear-gradient(180deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)",
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Story progress bars */}
          <div
            style={{
              display: "flex",
              gap: "4px",
              padding: "8px 10px 6px",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 2,
            }}
          >
            <div
              style={{
                flex: 1,
                height: "3px",
                borderRadius: "2px",
                background: "rgba(255,255,255,0.4)",
              }}
            />
            <div
              style={{
                flex: 1,
                height: "3px",
                borderRadius: "2px",
                background: "rgba(255,255,255,0.4)",
              }}
            />
          </div>
          {/* Story header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "36px 12px 8px",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 2,
            }}
          >
            {igAccount?.profilePicture ? (
              <img
                src={igAccount.profilePicture}
                alt={igUsername}
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  border: "2px solid #e1306c",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  border: "2px solid #e1306c",
                  background: "#1f2937",
                  flexShrink: 0,
                }}
              />
            )}
            <span
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#fff",
                textShadow: "0 1px 2px rgba(0,0,0,0.3)",
              }}
            >
              {igUsername}
            </span>
            <span
              style={{
                fontSize: "12px",
                color: "rgba(255,255,255,0.9)",
                marginLeft: "4px",
              }}
            >
              now
            </span>
          </div>
          {/* Story media / content area */}
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {mediaPreviewUrls.length > 0 ? (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {renderMediaGrid(1, "0")}
              </div>
            ) : (
              <span
                style={{
                  fontSize: "14px",
                  color: "rgba(255,255,255,0.9)",
                  textAlign: "center",
                  padding: "16px",
                }}
              >
                Add media to preview story
              </span>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="instagram-preview-container">
        {/* Header */}
        <div className="ig-header">
          <div className="ig-user-row">
            {igAccount?.profilePicture ? (
              <img
                src={igAccount.profilePicture}
                alt={igUsername}
                className="ig-avatar"
                style={{ objectFit: "cover" }}
              />
            ) : (
              <div className="ig-avatar" />
            )}
            <div className="ig-username">{igUsername}</div>
          </div>
          <div className="ig-more">
            <MoreHorizontal size={20} />
          </div>
        </div>

        {/* Image - only show when there is media */}
        {mediaPreviewUrls.length > 0 && (
          <div
            className="ig-image-placeholder"
            style={{
              background: "none",
              height: "auto",
              minHeight: "300px",
              overflow: "hidden",
            }}
          >
            {renderMediaGrid(4)}
          </div>
        )}

        {/* Actions */}
        <div className="ig-actions">
          <div className="ig-action-left">
            <span className="ig-icon">
              <Heart size={22} />
            </span>
            <span className="ig-icon">
              <MessageCircle size={22} />
            </span>
            <span className="ig-icon">
              <Send size={22} />
            </span>
          </div>
          <div className="ig-action-right">
            <span className="ig-icon">
              <Bookmark size={22} />
            </span>
          </div>
        </div>

        {/* Likes & Caption */}
        <div className="ig-likes">6,867 likes</div>
        <div className="ig-caption">
          <strong>{igUsername}</strong>{" "}
          {renderTextWithHashtags(
            postBody ||
              "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt. #innovation #design #creative"
          )}
        </div>
      </div>
    );
  };

  const renderThreadsPreview = () => {
    const threadsAccount = getPreviewAccount("threads");
    const displayName = threadsAccount?.displayName ?? "impret_admin";
    const username = threadsAccount?.username ?? "impret_admin";

    return (
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "24px",
          padding: "18px",
          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {threadsAccount?.profilePicture ? (
            <img
              src={threadsAccount.profilePicture}
              alt={displayName}
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "50%",
                objectFit: "cover",
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "50%",
                background: "#111827",
                flexShrink: 0,
              }}
            />
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "2px",
              }}
            >
              <span
                style={{
                  fontSize: "15px",
                  fontWeight: 700,
                  color: "#111827",
                }}
              >
                {displayName}
              </span>
              <img
                src={threadsIcon}
                alt="Threads"
                style={{ width: "16px", height: "16px" }}
                title="Threads"
              />
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "#6b7280",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              @{username}
            </div>
          </div>
        </div>

        <div
          style={{
            marginTop: "14px",
            fontSize: "15px",
            lineHeight: 1.55,
            color: "#111827",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {renderTextWithHashtags(
            postBody ||
              "Share a concise update with your audience, attach media, and keep the conversation moving."
          )}
        </div>

        {mediaPreviewUrls.length > 0 && (
          <div style={{ marginTop: "14px", overflow: "hidden", borderRadius: "18px" }}>
            {renderMediaGrid(4, "18px")}
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "18px",
            marginTop: "16px",
            paddingTop: "14px",
            borderTop: "1px solid #f1f5f9",
            color: "#6b7280",
            fontSize: "13px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Heart size={16} />
            <span>1.8K</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <MessageCircle size={16} />
            <span>214</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Repeat2 size={16} />
            <span>96</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Send size={16} />
            <span>Share</span>
          </div>
        </div>
      </div>
    );
  };

  const renderFacebookPreview = () => {
    const fbAccount = getPreviewAccount("facebook");
    const fbDisplayName = fbAccount?.displayName ?? "Nexus AI";
    return (
      <div className="facebook-preview-container">
        {/* Header */}
        <div className="fb-header">
          {fbAccount?.profilePicture ? (
            <img
              src={fbAccount.profilePicture}
              alt={fbDisplayName}
              className="fb-avatar"
              style={{ objectFit: "cover" }}
            />
          ) : (
            <div className="fb-avatar" />
          )}
          <div className="fb-user-info">
            <div className="fb-name">{fbDisplayName}</div>
            <div className="fb-time">
              2 hrs ·{" "}
              <Globe
                size={12}
                style={{ verticalAlign: "middle", opacity: 0.8 }}
              />
            </div>
          </div>
          <div className="fb-more">
            <MoreHorizontal size={20} />
          </div>
        </div>

        {/* Body */}
        <div className="fb-body">
          <p>
            {renderTextWithHashtags(
              postBody ||
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. #innovation #technology"
            )}
          </p>
        </div>

        {/* Image - only show when there is media */}
        {mediaPreviewUrls.length > 0 && (
          <div
            className="fb-image-placeholder"
            style={{ background: "none", height: "auto", overflow: "hidden" }}
          >
            {renderMediaGrid(4)}
          </div>
        )}

        {/* Footer Stats */}
        <div className="fb-stats">
          <div className="fb-reactions">
            <span className="fb-reaction-icon">
              <ThumbsUp size={14} />
            </span>
            <span className="fb-reaction-icon">
              <Heart size={14} />
            </span>
            <span className="fb-reaction-count">543</span>
          </div>
          <div className="fb-shares">123 Shares</div>
        </div>

        <div className="fb-divider"></div>

        {/* Action Buttons */}
        <div className="fb-actions">
          <div className="fb-action-btn">
            <ThumbsUp size={16} /> Like
          </div>
          <div className="fb-action-btn">
            <MessageCircle size={16} /> Comment
          </div>
          <div className="fb-action-btn">
            <Share2 size={16} /> Share
          </div>
        </div>
      </div>
    );
  };

  const renderLinkedInPreview = () => {
    const liAccount = getPreviewAccount("linkedIn") ?? getPreviewAccount("linkedin");
    const liDisplayName = liAccount?.displayName ?? "Nexus AI";
    return (
      <div className="linkedin-preview-container">
        {/* Header */}
        <div className="li-header">
          {liAccount?.profilePicture ? (
            <img
              src={liAccount.profilePicture}
              alt={liDisplayName}
              className="li-avatar"
              style={{ objectFit: "cover" }}
            />
          ) : (
            <div className="li-avatar" />
          )}
          <div className="li-user-info">
            <div className="li-name">{liDisplayName}</div>
            <div className="li-headline">AI Marketing Assistant</div>
            <div className="li-time">
              1w •
              <Globe
                size={12}
                style={{ verticalAlign: "middle", opacity: 0.8 }}
              />
            </div>
          </div>
          <div className="li-more">
            <MoreHorizontal size={20} />
          </div>
        </div>

        {/* Body */}
        <div className="li-body">
          <p>
            {renderTextWithHashtags(
              postBody ||
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. #innovation #technology"
            )}
          </p>
        </div>

        {/* Image - only show when there is media */}
        {mediaPreviewUrls.length > 0 && (
          <div
            className="li-image-placeholder"
            style={{ background: "none", height: "auto", overflow: "hidden" }}
          >
            {renderMediaGrid(5)}
          </div>
        )}

        {/* Footer Stats */}
        <div className="li-stats">
          <div className="li-reactions">
            <span className="li-reaction-icon">
              <ThumbsUp size={14} />
            </span>
            <span className="li-reaction-icon">
              <Heart size={14} />
            </span>
            <span className="li-reaction-icon">
              <Lightbulb size={14} />
            </span>
            <span className="li-reaction-count">1,034</span>
          </div>
          <div className="li-comments">15 comments • 17 reposts</div>
        </div>

        <div className="li-divider"></div>

        {/* Action Buttons */}
        <div className="li-actions">
          <div className="li-action-btn">
            <ThumbsUp size={16} /> Like
          </div>
          <div className="li-action-btn">
            <MessageCircle size={16} /> Comment
          </div>
          <div className="li-action-btn">
            <Repeat2 size={16} /> Repost
          </div>
          <div className="li-action-btn">
            <Send size={16} /> Send
          </div>
        </div>
      </div>
    );
  };

  const renderRedditPreview = () => {
    const redditAccount = getPreviewAccount("reddit");
    const redditUsername = redditAccount?.username ?? "NexusAI";
    return (
      <div className="reddit-preview-container">
        {/* Header */}
        <div className="rd-header">
          <div className="rd-sub-icon">
            <img
              src={redditIcon}
              alt="Reddit"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>
          <div className="rd-sub-name">{subreddits?.[0] ?? "r/Subreddit"}</div>
          <div className="rd-meta">• Posted by u/{redditUsername} 21h ago</div>
        </div>

        {/* Title */}
        <h3 className="rd-title">
          {postTitle ||
            "Lorem ipsum dolor sit amet, consectetur adipiscing elit"}
        </h3>
        {/* <div className="rd-flair">Tag</div> */}

        {/* Body/Image - only show when there is media */}
        {mediaPreviewUrls.length > 0 && (
          <div className="rd-content">
            <div
              className="rd-image-placeholder"
              style={{ background: "none", height: "auto", overflow: "hidden" }}
            >
              {renderMediaGrid(4)}
            </div>
          </div>
        )}

        {/* Text Content */}
        <div className="rd-text-body">
          <p>
            {renderTextWithHashtags(
              postBody ||
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."
            )}
          </p>
        </div>
      </div>
    );
  };

  const renderPreviewContent = () => {
    switch (activeFormTab) {
      case "twitter":
      case "x":
        return renderTwitterPreview();
      case "instagram":
        return renderInstagramPreview();
      case "threads":
        return renderThreadsPreview();
      case "facebook":
        return renderFacebookPreview();
      case "linkedIn":
      case "linkedin":
        return renderLinkedInPreview();
      case "reddit":
        return renderRedditPreview();
      default: {
        const defaultAccount = accountsSource?.accounts?.[0];
        const defaultDisplayName = defaultAccount?.displayName ?? "Nexus AI";
        return (
          <>
            <div className="preview-header">
              <div className="preview-avatar">
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    background: "#1f2937",
                    borderRadius: 8,
                  }}
                ></div>
              </div>
              <div className="preview-meta">
                <h4>{defaultDisplayName}</h4>
                <span>12,480 followers • Promoted</span>
              </div>
            </div>

            <div className="preview-body">
              <h3>{postTitle || "Lorem ipsum dolor sit amet consectetur"}</h3>
              <p>
                {renderTextWithHashtags(
                  postBody ||
                    "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris... see more"
                )}
              </p>
            </div>

            {mediaPreviewUrls.length > 0 && (
              <div
                className="preview-media-placeholder"
                style={{ overflow: "hidden" }}
              >
                {renderMediaGrid(4)}
              </div>
            )}
          </>
        );
      }
    }
  };

  return (
    <div className="step3-bg">
      {/* Launch overlay */}
      {isLaunching && (
        <div className="step3-launch-overlay">
          <div className="step3-launch-loader-card">
            <Loader2
              size={48}
              className="step3-launch-spinner"
              strokeWidth={2}
            />
            <h3 className="step3-launch-title">Launching your campaign</h3>
            <p className="step3-launch-subtitle">
              Uploading media and submitting to your selected platforms for processing…
            </p>
          </div>
        </div>
      )}

      <div className="step3-content">
        <div className="step3-header">
          <button className="back-btn" onClick={onBack}>
            <ArrowLeft
              size={18}
              style={{ verticalAlign: "middle", marginRight: "6px" }}
            />
            {isHistoryView ? "Back to History" : "Back to Prompt"}
          </button>
        </div>

        <div className="step3-main-container">
          {/* Left Column: Controls */}
          <div className="step3-left-col">
            <div className="step3-hero">
              <h1 className="cs-title">
                {isPublished
                  ? "Published Post"
                  : isScheduledView
                  ? "Scheduled Post"
                  : isDraftView
                  ? "Edit Draft"
                  : selectedVariation
                  ? "Refine & Preview"
                  : "Create Your Post"}
              </h1>
              <p
                className="cs-subtitle"
                style={{ margin: 0, textAlign: "left" }}
              >
                {isPublished ? (
                  "View your published post details."
                ) : isScheduledView ? (
                  "Review or update your scheduled post."
                ) : isDraftView ? (
                  <>
                    Edit your draft and{" "}
                    <span style={{ whiteSpace: "nowrap" }}>
                      publish when ready.
                    </span>
                  </>
                ) : selectedVariation ? (
                  <>
                    Fine-tune your generated content and{" "}
                    <span style={{ whiteSpace: "nowrap" }}>
                      schedule your launch.
                    </span>
                  </>
                ) : (
                  <>
                    Write your post from scratch, add media, and{" "}
                    <span style={{ whiteSpace: "nowrap" }}>
                      schedule your launch.
                    </span>
                  </>
                )}
              </p>
            </div>

            {/* Platform Selection */}
            <div className="form-section">
              <label className="section-label">Where are we posting?</label>

              {/* Platform Tabs with Add/Remove */}
              <div className="form-tabs-container">
                {selectedPlatforms.length === 0 ? (
                  <div className="no-platform-message">
                    Click the + button to add a platform
                  </div>
                ) : (
                  selectedPlatforms.map((platformId: string) => {
                    const platform = SOCIAL_PLATFORMS.find(
                      (p) => p.id === platformId
                    );
                    return (
                      <button
                        key={platformId}
                        className={`form-tab ${
                          activeFormTab === platformId ? "active" : ""
                        }`}
                        onClick={() => setActiveFormTab(platformId)}
                      >
                        <img
                          src={platform?.icon}
                          alt={platform?.name}
                          style={{ width: "16px", height: "16px" }}
                        />
                        {platform?.name}
                        <span
                          className="remove-platform-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePlatform(platformId);
                          }}
                        >
                          <X size={14} />
                        </span>
                      </button>
                    );
                  })
                )}

                {/* Add Platform Button */}
                <button
                  className="add-platform-btn"
                  onClick={() => setShowPlatformSelector(!showPlatformSelector)}
                >
                  <Plus size={20} />
                </button>
              </div>

              {/* Platform Selector Dropdown */}
              {showPlatformSelector && (
                <div className="platform-selector-dropdown">
                  {availablePlatforms
                    .filter(
                      (p: { platform: string }) =>
                        !selectedPlatforms.includes(p.platform)
                    )
                    .map((platform) => (
                      <div
                        key={platform._id}
                        className="platform-option"
                        onClick={() => {
                          togglePlatform(platform.platform);
                          setActiveFormTab(platform.platform);
                          setShowPlatformSelector(false);
                        }}
                      >
                        <img
                          src={
                            SOCIAL_PLATFORMS.find(
                              (p) => p.id === platform.platform
                            )?.icon
                          }
                          alt={
                            SOCIAL_PLATFORMS.find(
                              (p) => p.id === platform.platform
                            )?.name
                          }
                          style={{ width: "20px", height: "20px" }}
                        />
                        <span>
                          {
                            SOCIAL_PLATFORMS.find(
                              (p) => p.id === platform.platform
                            )?.name
                          }
                        </span>
                      </div>
                    ))}
                  {availablePlatforms.length === 0 ? (
                    <div className="platform-option-empty">
                      No connected platforms. Connect one first.
                    </div>
                  ) : selectedPlatforms.length === availablePlatforms.length ? (
                    <div className="platform-option-empty">
                      All platforms selected
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Empty State */}
            {selectedPlatforms.length === 0 && (
              <div className="no-platform-selected">
                <div className="empty-state-icon">
                  <FileText size={32} />
                </div>
                <h3>Select a platform to get started</h3>
                <p>
                  {availablePlatforms.length === 0
                    ? "No connected platforms. Connect one first."
                    : "Click the + button above to add your first platform"}
                </p>
              </div>
            )}

            {/* Platform-Specific Form Content */}
            {selectedPlatforms.length > 0 && (
              <div
                style={
                  isPublished
                    ? { pointerEvents: "none", opacity: 0.6 }
                    : undefined
                }
              >
                {(() => {
                  const platformProps = {
                    postTitle,
                    setPostTitle,
                    postBody,
                    setPostBody,
                    mediaPreviewUrls,
                    mediaMetadata,
                    handleMediaUpload,
                    removeMedia,
                    fileInputRef,
                    draggedIndex,
                    handleImageDragStart,
                    handleImageDragOver,
                    handleImageDrop,
                    handleImageDragEnd,
                    handleFileChange,
                    handleDragOver,
                    handleDrop,
                    threadEnabled,
                    setThreadEnabled,
                    threadTweets,
                    setThreadTweets,
                    isStory,
                    setIsStory,
                    useCommonMediaForAllPlatforms,
                    setUseCommonMediaForAllPlatforms,
                    showCommonMediaToggle:
                      provider === "postforme" && selectedPlatforms.length > 1,
                    subreddits,
                    setSubreddits,
                    searchSubreddit,
                    xLongFormEnabled,
                    setXLongFormEnabled,
                  };

                  switch (activeFormTab) {
                    case "twitter":
                    case "x":
                      return (
                        <TwitterForm
                          {...platformProps}
                          postBody={tweetBody}
                          setPostBody={setTweetBody}
                          maxCharacters={xMaxCharacters}
                          showXLongFormToggle
                          disableThreads={provider === "postforme"}
                        />
                      );
                    case "instagram":
                      return <InstagramForm {...platformProps} />;
                    case "threads":
                      return (
                        <TwitterForm
                          {...platformProps}
                          platformName="Threads"
                          platformIcon={threadsIcon}
                          contentLabel="Post Content"
                          contentPlaceholder="Write your Threads post here..."
                          contentTypeName="Post"
                          maxCharacters={500}
                          warningPlatformLabel="Threads"
                          allowThreadComposer={false}
                        />
                      );
                    case "facebook":
                      return <FacebookForm {...platformProps} />;
                    case "linkedIn":
                    case "linkedin":
                      return <LinkedInForm {...platformProps} />;
                    case "reddit":
                      return <RedditForm {...platformProps} />;
                    default:
                      return null;
                  }
                })()}
              </div>
            )}

            {/* Scheduling - Common across all platforms */}
            {selectedPlatforms.length > 0 && (
              <div
                className="form-section"
                style={
                  isPublished
                    ? { pointerEvents: "none", opacity: 0.6 }
                    : undefined
                }
              >
                <label className="section-label">Scheduling</label>
                <div className="scheduling-options">
                  {!isScheduledView && (
                    <button
                      type="button"
                      className={`schedule-option ${
                        scheduleOption === "now" ? "active" : ""
                      }`}
                      onClick={() => setScheduleOption("now")}
                    >
                      Post Now
                    </button>
                  )}
                  <button
                    type="button"
                    className={`schedule-option ${
                      scheduleOption === "later" ? "active" : ""
                    }`}
                    disabled={isScheduleLaterDisabled}
                    title={
                      isSchedulingLocked
                        ? "Scheduling is available on paid plans."
                        : undefined
                    }
                    onClick={() => {
                      if (isScheduleLaterDisabled) {
                        return;
                      }
                      setScheduleOption("later");
                    }}
                  >
                    Schedule for Later
                  </button>
                </div>

                {isSchedulingLocked && (
                  <div
                    style={{
                      marginTop: "16px",
                      padding: "16px 18px",
                      borderRadius: "14px",
                      border: "1px solid rgba(99,102,241,0.16)",
                      background:
                        "linear-gradient(135deg, rgba(79,70,229,0.08), rgba(14,165,233,0.08))",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "15px",
                          fontWeight: 700,
                          color: "#111827",
                          marginBottom: "6px",
                        }}
                      >
                        Scheduling is a paid feature
                      </div>
                      <p
                        style={{
                          margin: 0,
                          color: "#4b5563",
                          lineHeight: 1.6,
                          fontSize: "14px",
                        }}
                      >
                        Upgrade to schedule posts for later. Free plan
                        users can still publish immediately.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={openUpgradeModal}
                      disabled={!isConfigured || isOpeningCheckout}
                      style={{
                        border: "none",
                        borderRadius: "999px",
                        padding: "10px 16px",
                        background: "#4f46e5",
                        color: "#fff",
                        fontWeight: 600,
                        cursor:
                          !isConfigured || isOpeningCheckout
                            ? "not-allowed"
                            : "pointer",
                        opacity: !isConfigured || isOpeningCheckout ? 0.7 : 1,
                      }}
                    >
                      {isOpeningCheckout ? "Opening checkout..." : "Upgrade"}
                    </button>
                  </div>
                )}

                {scheduleOption === "now" && !isScheduledView && (
                  <div className="launch-info">
                    <span className="info-icon">
                      <Info size={16} />
                    </span>
                    <span className="info-text">
                      Your campaign will be submitted immediately to{" "}
                      {selectedPlatforms
                        .map(
                          (id: string) =>
                            SOCIAL_PLATFORMS.find((p) => p.id === id)?.name
                        )
                        .join(", ")}
                      . Final publish status may take a few seconds.
                    </span>
                  </div>
                )}

                {scheduleOption === "later" && (
                  <div
                    className="date-picker-container"
                    style={{ marginTop: "16px" }}
                  >
                    <label
                      className="section-label"
                      style={{ fontWeight: 500, color: "#6b7280" }}
                    >
                      Select Date & Time{" "}
                      <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <DatePicker
                      selected={scheduledDate}
                      onChange={(date: Date | null) => setScheduledDate(date)}
                      showTimeSelect
                      timeIntervals={30}
                      dateFormat="MMMM d, yyyy h:mm aa"
                      className="step3-input"
                      placeholderText="Select date and time"
                      minDate={new Date()}
                      filterTime={(time) => time.getTime() >= Date.now()}
                      withPortal={true}
                      required
                    />
                  </div>
                )}
              </div>
            )}

            {/* Validation Status */}
            {selectedPlatforms.length > 0 && (
              <>
                {isLaunchDisabled ? (
                  <div
                    style={{
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      marginTop: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: validationIssues.length > 0 ? "8px" : "0",
                      }}
                    >
                      <AlertTriangle
                        size={18}
                        style={{ color: "#991b1b", flexShrink: 0 }}
                      />
                      <span
                        style={{
                          fontWeight: 600,
                          color: "#991b1b",
                          fontSize: "14px",
                        }}
                      >
                        {validationIssues.length === 1
                          ? "1 issue to fix:"
                          : `${validationIssues.length} issues to fix:`}
                      </span>
                    </div>
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: "28px",
                        color: "#991b1b",
                        fontSize: "13px",
                        lineHeight: "1.6",
                      }}
                    >
                      {validationIssues.map((issue: string, index: number) => (
                        <li key={index}>{issue}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div
                    style={{
                      background: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      marginTop: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Check
                      size={18}
                      style={{ color: "#166534", flexShrink: 0 }}
                    />
                    <span
                      style={{
                        fontWeight: 600,
                        color: "#166534",
                        fontSize: "14px",
                      }}
                    >
                      Ready to{" "}
                      {scheduleOption === "now" ? "launch" : "schedule"}!
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Action Buttons */}
            {selectedPlatforms.length > 0 && (
              <div className="step3-action-buttons">
                {/* PUBLISHED: all buttons disabled */}
                {isPublished ? (
                  <>
                    <button
                      type="button"
                      className="draft-btn"
                      disabled
                      style={{ opacity: 0.5, cursor: "not-allowed" }}
                    >
                      <X
                        size={18}
                        style={{ verticalAlign: "middle", marginRight: "6px" }}
                      />
                      Cancel
                    </button>
                    <button
                      className="launch-btn"
                      disabled
                      style={{ opacity: 0.5, cursor: "not-allowed" }}
                    >
                      <Rocket
                        size={18}
                        style={{ verticalAlign: "middle", marginRight: "6px" }}
                      />
                      Published
                    </button>
                  </>
                ) : isScheduledView ? (
                  /* SCHEDULED: Update Schedule only */
                  <button
                    className="launch-btn"
                    onClick={handleLaunch}
                    disabled={
                      isLaunchDisabled ||
                      isLaunching ||
                      (isSchedulingLocked && isScheduledView)
                    }
                    style={{
                      opacity:
                        isLaunchDisabled ||
                        isLaunching ||
                        (isSchedulingLocked && isScheduledView)
                          ? 0.5
                          : 1,
                      cursor:
                        isLaunchDisabled ||
                        isLaunching ||
                        (isSchedulingLocked && isScheduledView)
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    {isLaunching ? (
                      <>
                        <Loader2
                          size={18}
                          className="launch-btn-spinner"
                          style={{
                            verticalAlign: "middle",
                            marginRight: "6px",
                            flexShrink: 0,
                          }}
                        />
                        Updating…
                      </>
                    ) : (
                      <>
                        <Rocket
                          size={18}
                          style={{
                            verticalAlign: "middle",
                            marginRight: "6px",
                          }}
                        />
                        {isSchedulingLocked
                          ? "Upgrade to Edit Schedule"
                          : "Update Schedule"}
                      </>
                    )}
                  </button>
                ) : isDraftView ? (
                  /* DRAFT: Cancel + Launch/Schedule */
                  <>
                    <button
                      type="button"
                      className="draft-btn"
                      onClick={onBack}
                    >
                      <X
                        size={18}
                        style={{ verticalAlign: "middle", marginRight: "6px" }}
                      />
                      Cancel
                    </button>
                    <button
                      className="launch-btn"
                      onClick={handleLaunch}
                      disabled={isLaunchDisabled || isLaunching}
                      style={{
                        opacity: isLaunchDisabled || isLaunching ? 0.5 : 1,
                        cursor:
                          isLaunchDisabled || isLaunching
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      {isLaunching ? (
                        <>
                          <Loader2
                            size={18}
                            className="launch-btn-spinner"
                            style={{
                              verticalAlign: "middle",
                              marginRight: "6px",
                              flexShrink: 0,
                            }}
                          />
                          Launching…
                        </>
                      ) : (
                        <>
                          <Rocket
                            size={18}
                            style={{
                              verticalAlign: "middle",
                              marginRight: "6px",
                            }}
                          />
                          {scheduleOption === "now"
                            ? "Launch Campaign"
                            : "Schedule"}
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  /* NORMAL: Draft + Launch/Schedule */
                  <>
                    <button
                      type="button"
                      className="draft-btn"
                      onClick={handleSaveDraft}
                      disabled={isLaunchDisabled || isLaunching}
                      style={{
                        opacity: isLaunchDisabled || isLaunching ? 0.5 : 1,
                        cursor:
                          isLaunchDisabled || isLaunching
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      <FileEdit
                        size={18}
                        style={{ verticalAlign: "middle", marginRight: "6px" }}
                      />
                      Draft
                    </button>
                    <button
                      className="launch-btn"
                      onClick={handleLaunch}
                      disabled={isLaunchDisabled || isLaunching}
                      style={{
                        opacity: isLaunchDisabled || isLaunching ? 0.5 : 1,
                        cursor:
                          isLaunchDisabled || isLaunching
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      {isLaunching ? (
                        <>
                          <Loader2
                            size={18}
                            className="launch-btn-spinner"
                            style={{
                              verticalAlign: "middle",
                              marginRight: "6px",
                              flexShrink: 0,
                            }}
                          />
                          Launching…
                        </>
                      ) : (
                        <>
                          <Rocket
                            size={18}
                            style={{
                              verticalAlign: "middle",
                              marginRight: "6px",
                            }}
                          />
                          {scheduleOption === "now"
                            ? "Launch Campaign"
                            : "Schedule"}
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right Column: Preview */}
          <div className="step3-right-col">
            <div className="preview-sticky-wrapper">
              {selectedPlatforms.length === 0 ? (
                <div className="preview-empty-state">
                  <div className="preview-empty-icon">
                    <Eye size={32} />
                  </div>
                  <h3>Preview</h3>
                  <p>Select a platform to see your post preview</p>
                </div>
              ) : (
                <div className="preview-card">{renderPreviewContent()}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      {upgradeModal}

      {/* Toast Notifications */}
      {toast && (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={4000}
        />
      )}
    </div>
  );
};

export default SocialPostScreen;
